import { useAtom } from '@reatom/npm-react'
import React, { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, Tooltip } from '@blade/ui-kit'
import { BlacklistCurrenciesDialog } from '@root/features/blacklist/blacklist-currencies/ui/blacklist-currencies-dialog'
import { exploreColumnsAtom } from '@root/features/explore/atoms/reatom-explore-layout'
import { QuickBuyInput } from '@root/features/trade/quick-buy/ui'
import { EyeClosedIcon } from '@root/shared/ui-kit'

import { ExploreLayoutSelect } from '../../explore-layout-select/explore-layout-select'

import { ExploreFeed } from './explore-feed'

import type { ClassNameProps } from '@root/shared/react'

type ExplorePairsProps = {} & ClassNameProps

export const ExplorePairs: React.FC<ExplorePairsProps> = memo(
  ({ className }) => {
    const { t } = useTranslation()
    const [columns] = useAtom(exploreColumnsAtom)
    const [blackListOpen, setBlackListOpen] = useState(false)

    return (
      <Root>
        <Frame className={className}>
          <Heading>
            <HeaderStart>
              <HeaderTitle>{t('common.explore')}</HeaderTitle>
              <ExploreLayoutSelect />
            </HeaderStart>
            <HeaderEnd>
              <Tooltip content={t('blacklist.currencies.tooltip')}>
                <Button
                  variant="secondary"
                  radius="rounded"
                  size="h32"
                  iconLeft={<EyeClosedIcon />}
                  onClick={() => setBlackListOpen(true)}
                />
              </Tooltip>
              <QuickBuyInput section="exploreFeeds" />
            </HeaderEnd>
          </Heading>
          <Body>
            {columns.map((feed, column) => (
              <ExploreFeed key={column} column={column} feed={feed} />
            ))}
          </Body>
        </Frame>
        <BlacklistCurrenciesDialog
          open={blackListOpen}
          onOpenChange={setBlackListOpen}
        />
      </Root>
    )
  },
)

const Root = styled.div`
  padding: 10px;
  padding-block-end: 15px;
`

const Heading = styled.header`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding-inline: 18px;
  height: 50px;

  display: grid;
  align-content: center;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
`

const HeaderStart = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 12px;
`
const HeaderEnd = styled(HeaderStart)`
  gap: 8px;
`

const Frame = styled.div`
  border: 1px solid var(--background-3);
  background: var(--background-1);

  border-radius: 12px;
  display: grid;
  grid-template-rows: auto var(--explore-pairs-body-height, 70vh);
`

const Body = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);

  gap: 6px;
  padding: 5px;
  padding-block-start: 0;
  min-height: 0;
`

const HeaderTitle = styled.div`
  color: var(--text-12);

  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`
